// CreditBanner — the admin early-warning pill for a workspace approaching
// zero credits (tickets/fable.md, Nika 2026-08-13: "notifications + banner
// to admin when its approaching to end - that links to credit usage page").
//
// Shows for admins only, at guard status LOW or CRITICAL (fractions of the
// monthly grant — functions/creditGuard.js; the figures themselves come
// from sdk.getCreditBalance, never literals). EXHAUSTED belongs to
// CreditBlocker while that notice is on screen — but it is dismissible now
// (CREDIT-BLOCK-1 defect #3), so once dismissed this pill takes over as the
// persistent exhausted indicator. Exactly one of the two is ever visible, so
// "they never co-render" still holds. Status UNKNOWN shows nothing at all:
// a balance nobody could read must not warn any more than it may block.
// Hidden on /admin/usage
// itself (the page IS the destination — tickets/opus.md "credits-exhausted
// UX": the CTA below used to route to the never-registered `/credits`, a
// silent no-op redirect to `/`) and after a per-(workspace, month, level)
// dismissal — an escalation from low → critical re-surfaces it.
//
// Chrome: the house bottom-left glass pill (CanvasHelpCard — the
// DemoClaimBanner/PersonaPill corner idiom), zero layout shift by
// construction (`display:'contents'` wrapper + fixed descendant). When a
// persona session is active the pill lifts one step up so it never overlaps
// PersonaPill's own bottom-left slot.
//
// UI-TUTORIAL-NOTIFICATION-FAMILY-1: CreditWarningPill (below) extends
// TutorialNotification (same canvas-card-glass surface CanvasHelpCard
// already gave it) so the "Top up" CTA goes through the shared
// TutorialNotificationButtons row instead of a hand-rolled button.
// `useCookie: false` — this pill's OWN escalation-aware
// `_creditBannerDismissed`/`credit_banner_${wsId}` logic (below) is its
// entire dismiss contract; it must not also gate behind the family's
// generic tutorial-cookie. The dismiss-X stays its own sibling, keyed
// `CloseButton` (bare-key auto-extend, dropped the explicit `extends`
// audit_component's FA806 flagged) rather than folding into the action
// row — TutorialNotificationButtons is for labeled text+icon CTAs, not a
// bare close-X. `CreditWarningPill` is a real top-level component, not a
// nested child with an `extends` audit_component would flag (FA806) — same
// fix RemoteUpdateBanner/InfraDemoBanner already use, both top-level
// exports with their own differently-named `extends`. Named
// `CreditWarningPill`, not the generic `CanvasHelpCard` it used to extend
// (would collide at the DOM level with the real, separate CanvasHelpCard
// instances elsewhere — MEMORY project_two_elements_share_one_data_key) and
// not `DemoClaimBanner`'s own pill's name either — the two can legitimately
// be on screen at once (a demo admin low on credits).
//
// NO `position`/inset props on this element — deliberately. `extends:
// 'TutorialNotification' extends 'Navbar'` (company/packages/brand/
// components/Navbar.js), whose OWN base bakes in `position:'sticky',
// top:'0'` as its identity. Overriding `position:'fixed'` here does not
// reliably cancel Navbar's `top:'0'` (measured live on the identical
// DemoClaimBanner pattern: rendered as a near-fullscreen box instead of a
// compact pill, even with an explicit `top:'auto'` override attempted).
// Fix: positioning lives on `CreditBanner`'s own `CreditPillDock` wrapper
// below instead — a plain box with no Navbar ancestry to fight.
export const CreditWarningPill = {
  extends: 'TutorialNotification',
  useCookie: false,
  // The predicate itself moved to `functions/creditGuard.js`
  // (`creditPillVisible`) — the DOCK wrapper below has to ask the very same
  // question before it may claim any geometry, and two copies of a
  // visibility rule is exactly how the wrapper ended up covering a live AI
  // thread with an empty panel
  // (WS-ASSISTANT-DRAWER-COVERED-BY-EMPTY-CREDIT-DOCK-1). One source of
  // truth, reached through the registry so it survives the frank lift.
  //
  // FA515: the predicate reads its state keys behind short-circuiting
  // conditionals, so the dependency tracker cannot see them from the factory
  // body alone — declared explicitly. The list mirrors CreditBanner's own
  // `subscribeTo` below: the wrapper's subscription covers whether IT
  // re-renders, this element's reactive factory needs its own declaration
  // regardless, and an under-declared list is what leaves a stale gate on
  // screen.
  hideDeps: [
    'signedIn',
    'currentPath',
    'roles',
    'workspace',
    '_creditGuard',
    '_creditsShort',
    '_creditBannerDismissed',
    '_creditBlockerDismissed'
  ],
  hide: (el, s) => !el.call('creditPillVisible', el, s.root),
  flow: 'x',
  align: 'center',
  gap: 'Z1',
  padding: 'Z A',
  round: 'radiusControl',
  boxShadow: 'none',
  width: 'auto',
  // Hgroup nulled too: single-line horizontal pill (own Dot/Label below),
  // not TutorialNotification's default vertical icon+title+descr card.
  Lottie: null,
  Hgroup: null,

  Dot: {
    tag: 'span',
    width: 'Y',
    height: 'Y',
    minWidth: 'Y',
    round: '50%',
    ariaHidden: 'true',
    background: (el, s) => {
      // A shortfall is a warning, not an emergency: the workspace still has
      // credits, it is the one request that does not fit.
      if (el.call('creditShortState', el, s.root)) return 'warningInk'
      const guard = el.call('creditGuardState', s.root)
      const st = guard?.status
      return st === 'critical' || st === 'exhausted' ? 'dangerInk' : 'warningInk'
    }
  },

  Label: {
    tag: 'span',
    fontSize: 'Z1',
    color: 'title',
    // The sentence is the whole message — for a held shortfall what the
    // request needs and, for a member, who to ask — so it wraps rather than
    // being cut off behind an ellipsis. The short low/critical/exhausted lines
    // fit on one row either way.
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    text: (el, s) => {
      // A held SHORTFALL (functions/creditGuard.js creditShortState): what the
      // last refused request needs against what the workspace has. The figures
      // are formatted like every other credit count here; the way out is the
      // button beside it (below), which depends on who is looking.
      const short = el.call('creditShortState', el, s.root)
      if (short) {
        return el
          .call('polyglot', 'workspace.creditBanner.short')
          .replace('{needed}', () => Number(short.needed).toLocaleString('en-US'))
          .replace('{available}', () => Number(short.available).toLocaleString('en-US'))
      }
      const guard = el.call('creditGuardState', s.root)
      if (guard?.status === 'exhausted')
        return el.call('polyglot', 'workspace.creditBanner.exhausted')
      const n = Number(guard?.total || 0).toLocaleString('en-US')
      return el
        .call(
          'polyglot',
          guard?.status === 'critical'
            ? 'workspace.creditBanner.critical'
            : 'workspace.creditBanner.low'
        )
        .replace('{count}', n)
    }
  },

  // Real Button/CanvasButton semantics via the shared action row instead
  // of a hand-rolled `tag:'button'` — one button, so `children` is
  // length-1, same as the family's own single-CTA precedent.
  TutorialNotificationButtons: {
    height: 'auto',
    childProps: {
      background: 'transparent',
      border: 'none',
      padding: 'V Y',
      round: 'radiusControl',
      flex: '0 0 auto',
      cursor: 'pointer',
      fontSize: 'Z1',
      fontWeight: '700',
      color: 'title',
      whiteSpace: 'nowrap',
      ':hover': { background: 'hover' },
      // CanvasButton/Button default to `aspect-ratio: 1/1` (an icon-only
      // square/circle button). This CTA has text and no icon, so without
      // this override it renders near-square with border-radius:999px
      // rounding it into a circle instead of a pill (measured live on the
      // identical DemoClaimBanner pattern).
      aspectRatio: 'auto'
    },
    children: [
      {
        // The way out, by role. An ADMIN holding a shortfall adds credits (the
        // credit page); a MEMBER, who has no such page (house rule: no fake
        // affordances), asks the admins — a real in-app request, once per
        // refusal (functions/creditGuard.js askAdminsForCredits), after which
        // the button says so. Without a shortfall it is the usual Top up.
        text: (el, s) => {
          const short = el.call('creditShortState', el, s.root)
          return el.call(
            'polyglot',
            !short
              ? 'workspace.creditBanner.topUp'
              : short.admin
                ? 'workspace.creditBanner.addCredits'
                : short.asked
                  ? 'workspace.creditBanner.asked'
                  : 'workspace.creditBanner.askAdmin'
          )
        },
        disabled: (el, s) => {
          const short = el.call('creditShortState', el, s.root)
          return !!short && !short.admin && short.asked ? true : null
        },
        title: (el, s) => {
          const short = el.call('creditShortState', el, s.root)
          return el.call(
            'polyglot',
            short && !short.admin
              ? 'workspace.creditBanner.askAdminTitle'
              : 'workspace.creditBanner.topUpTitle'
          )
        },
        onClick: (e, el, s) => {
          const short = el.call('creditShortState', el, s.root)
          if (short && !short.admin) return el.call('askAdminsForCredits', el, s.root)
          el.router('/admin/usage', el.getRoot())
        }
      }
    ]
  },

  // THE house close X (brand CloseButton — F2, one X for every panel).
  // Bare-key auto-extend (key matches CloseButton exactly) — no `extends`
  // needed, and CloseButton is meant to be interchangeable everywhere it's
  // used, so there's no meaningful-name reason to alias it the way
  // CanvasHelpCard needed one.
  CloseButton: {
    title: (el) => el.call('polyglot', 'workspace.creditBanner.dismissTitle'),
    'aria-label': (el) => el.call('polyglot', 'workspace.creditBanner.dismissAria'),
    // X position fix (same ask): the 36px house circle inflated this slim
    // one-line pill's height and floated in the full `A` right-padding air
    // pocket. Negative margins tuck it — the hit circle overhangs the
    // pill's vertical padding instead of growing it, and sits flush toward
    // the right edge; the 44pt coarse-pointer slop is untouched.
    margin: '-Y2 -Z1 -Y2 0',
    onClick: (e, el, s) => {
      e.stopPropagation()
      const root = s.root
      // A shortfall closes itself, not the month's low-credit ladder below:
      // the record stays (the usage page keeps telling the truth about it),
      // only the pill is closed, until a NEW refusal opens it again.
      if (el.call('creditShortState', el, root)) {
        root.update(
          { _creditsShort: { ...root._creditsShort, dismissed: true } },
          { preventFetch: true }
        )
        return
      }
      const guard = el.call('creditGuardState', root)
      const wsId = root.workspace?.id || root.workspace?._id || 'ws'
      const period = new Date().toISOString().slice(0, 7)
      // Same ladder as `hide:` above (exhausted:3 / critical:2 / low:1) —
      // writing a lower level than the state being dismissed leaves the
      // hide unsatisfied and renders the X a no-op (measured: the exhausted
      // takeover pill could never be dismissed while this wrote 1).
      const level = guard?.status === 'exhausted' ? 3 : guard?.status === 'critical' ? 2 : 1
      const seen = `${period}:${level}`
      el.call('setPref', `credit_banner_${wsId}`, seen)
      root.update({ _creditBannerDismissed: seen }, { preventFetch: true })
    }
  }
}

export const CreditBanner = {
  tag: 'div',
  display: 'contents',
  subscribeTo: [
    '_creditGuard',
    '_creditsShort',
    'currentPath',
    'roles',
    'workspace',
    'signedIn',
    '_creditBannerDismissed',
    '_creditBlockerDismissed'
  ],
  // Plain positioning wrapper — NOT extending TutorialNotification/Navbar,
  // so it carries none of Navbar's baked-in `position:'sticky', top:'0'`
  // to fight (see CreditWarningPill's own comment above for why this split
  // exists).
  CreditPillDock: {
    // WS-ASSISTANT-DRAWER-COVERED-BY-EMPTY-CREDIT-DOCK-1 — the dock is a
    // POSITIONING wrapper for the pill, so it must not exist as a box when
    // there is no pill to position. It used to render unconditionally (the
    // visibility gate lived only on the child), and shellApp's docked-
    // assistant stylesheet then gave that always-present, EMPTY wrapper the
    // drawer's whole rect at z-index 10001 with an opaque background: on
    // every workspace that is not out of credits — the common case — an
    // invisible panel covered the entire AI thread (answer, source chips,
    // confidence pill). Same predicate as the pill, same source
    // (`creditPillVisible`), so the two can never disagree.
    hideDeps: [
      'signedIn',
      'currentPath',
      'roles',
      'workspace',
      '_creditGuard',
      '_creditsShort',
      '_creditBannerDismissed',
      '_creditBlockerDismissed'
    ],
    hide: (el, s) => !el.call('creditPillVisible', el, s.root),
    // …and PUBLISH what it is carrying, so the shell stylesheet can key the
    // docked-assistant geometry on credit state instead of on the mere
    // presence of this element. 'cover' (out of credits — the drawer is a
    // dead surface, the notice takes its box), 'pill' (low/critical — the
    // assistant still works, so the warning must never cover a live thread),
    // absent (no pill: no geometry, nothing to paint, nothing to hit-test).
    // The reacts-in-CSS idiom the shell already uses for <html
    // data-assistant-dock>; see app.js for the rules that consume it.
    attrDeps: {
      'data-credit-dock': [
        'signedIn',
        'currentPath',
        'roles',
        'workspace',
        '_creditGuard',
        '_creditsShort',
        '_creditBannerDismissed',
        '_creditBlockerDismissed'
      ]
    },
    attr: {
      'data-credit-dock': (el, s) => el.call('creditDockMode', el, s.root)
    },
    position: 'fixed',
    // Toast-anchor family (2026-09-02 per Nika "move CreditWarningPill
    // above AI textarea ... if its out of credits AI is obviously blocked
    // so its good UI decision") — the same centered above-composer slot
    // CallInvitationBanner took: the pill now reads as a status OF the
    // composer it gates. Replaces the old bottom-LEFT corner dock (and its
    // PersonaPill step — the persona pill keeps that corner; no collision
    // from the center slot). If the call banner and this pill ever
    // co-render they share the anchor — accepted: both are transient, and
    // the call banner outranks visually by size.
    left: '50%',
    transform: 'translateX(-50%)',
    right: 'auto',
    bottom: 'C1',
    zIndex: '1500',
    display: 'flex',
    width: 'auto',
    maxWidth: 'calc(100dvw - var(--spacing-B))',
    '@mobileL': {
      left: 'A',
      right: 'A',
      transform: 'none',
      justifyContent: 'center'
    },
    // Bare-key auto-extend resolves CreditWarningPill (this file, above) —
    // no `extends` needed since the key name matches exactly.
    CreditWarningPill: {}
  }
}
